import React, { useState } from 'react';
import { BookOpen, GraduationCap, Briefcase, ExternalLink } from 'lucide-react';

const Portal = () => {
  const [userType, setUserType] = useState('student');

  // Student portals data
  const studentPortals = [
    { name: "ePortal", url: "https://portal.fedpolybida.edu.ng/", icon: "📊", description: "Main student portal for academic activities" },
    { name: "eResult Portal", url: "https://results.fedpolybida.edu.ng/index.html", icon: "📝", description: "Check your examination results" },
    { name: "ePayments Portal", url: "https://portal.fedpolybida.edu.ng/epayment/index.php", icon: "💳", description: "Make payments for tuition and fees" },
    { name: "Academic Calendar", url: "#", icon: "📅", description: "View academic calendar and important dates" },
    { name: "OLearn LMS", url: "https://elearning.fedpolybida.edu.ng/", icon: "🎓", description: "Access learning management system" },
    { name: "NELFUND Portal", url: "https://portal.nelf.gov.ng/auth/login/", icon: "💰", description: "Access Student Load Portal for NelFund" },
  ];

  // Staff portals data
  const staffPortals = [
    { name: "eResult Portal", url: "https://results.fedpolybida.edu.ng/index_staff.php", icon: "📝", description: "Upload and manage student results" },
    { name: "Payslip Portal", url: "https://payslip.fedpolybida.edu.ng/", icon: "💰", description: "Access salary information and payslips" },
    { name: "OLearn LMS", url: "https://elearning.fedpolybida.edu.ng/", icon: "🎓", description: "Manage courses and learning materials" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <BookOpen className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Student & Staff Portals</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Direct access to all institutional portals and resources
          </p>
        </div>

        {/* User Type Selection */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setUserType('student')}
              className={`px-6 py-3 text-sm font-medium rounded-l-lg ${
                userType === 'student' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <GraduationCap className="w-5 h-5 inline mr-2" />
              Student Portals
            </button>
            <button
              type="button"
              onClick={() => setUserType('staff')}
              className={`px-6 py-3 text-sm font-medium rounded-r-lg ${
                userType === 'staff' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Briefcase className="w-5 h-5 inline mr-2" />
              Staff Portals
            </button>
          </div>
        </div>

        {/* Portal Links Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            {userType === 'student' ? 'Student Portals' : 'Staff Portals'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(userType === 'student' ? studentPortals : staffPortals).map((portal, index) => (
              <a
                key={index}
                href={portal.url}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-transparent hover:border-blue-200 group transform hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-2xl">{portal.icon}</span>
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{portal.name}</h3>
                <p className="text-gray-600 text-sm">{portal.description}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Quick Info Section */}
        <div className="bg-blue-50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">Need Help Accessing Portals?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-3">For Students</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Use your student ID and password to login</li>
                <li>Reset password through the IT helpdesk</li>
                <li>Access requires active student status</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">For Staff</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Use your staff ID and assigned credentials</li>
                <li>Contact admin for access issues</li>
                <li>Some portals require special permissions</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Support Contact */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Support</h3>
          <p className="text-gray-600 mb-4">Having issues with portal access? Contact our IT helpdesk</p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <a href="mailto:it-support@polytechnic.edu" className="text-blue-600 hover:text-blue-800 font-medium">
              it-support@polytechnic.edu
            </a>
            <span className="text-gray-400 hidden sm:block">•</span>
            <a href="tel:+234123456789" className="text-blue-600 hover:text-blue-800 font-medium">
              +234 123 456 789
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portal;